/**
 * Storage Compatibility Engine
 *
 * Checks storage compatibility including:
 * - M.2 slot compatibility and lane allocation
 * - SATA port availability
 * - Boot support compatibility
 * - Performance optimization recommendations
 */

import type {
  CompatibilityChecker,
  CompatibilityResult,
  CompatibilityContext
} from '../types/CompatibilityResult'
import type { StorageModel } from '../../models/storage/storage-model'
import type { MotherboardModel } from '../../models/motherboard/motherboard-model'
import type { CPUModel } from '../../models/cpu/cpu-model'

/**
 * M.2 Storage Compatibility Checker
 */
export class M2StorageCompatibilityChecker implements CompatibilityChecker<StorageModel, MotherboardModel> {
  check(storage: StorageModel, motherboard: MotherboardModel, _context?: CompatibilityContext): CompatibilityResult[] {
    const results: CompatibilityResult[] = []

    if (storage.category !== 'NVMe SSD') {
      return results // Only check M.2 NVMe drives
    }

    // M.2 slot availability check
    const slotResult = this.checkM2SlotAvailability(storage, motherboard)
    results.push(slotResult)

    // PCIe lane compatibility
    const laneResult = this.checkPCIeLaneCompatibility(storage, motherboard)
    if (laneResult) results.push(laneResult)

    // Form factor compatibility
    const formFactorResult = this.checkFormFactorCompatibility(storage, motherboard)
    if (formFactorResult) results.push(formFactorResult)

    // Boot support check
    const bootResult = this.checkBootSupport(storage, motherboard)
    if (bootResult) results.push(bootResult)

    return results
  }

  getScore(storage: StorageModel, motherboard: MotherboardModel): number {
    if (storage.category !== 'NVMe SSD') return 100 // SATA drives are generally compatible

    const m2Slots = motherboard.m2Slots || 0
    const supportsPCIe4 = this.supportsPCIe4(motherboard)
    const storageIsPCIe4 = storage.interface?.includes('PCIe 4.0') || false

    let score = 100

    if (m2Slots === 0) return 0 // No M.2 slots

    // Reduce score if using PCIe 4.0 drive on PCIe 3.0 slot
    if (storageIsPCIe4 && !supportsPCIe4) {
      score -= 20
    }

    // Reduce score if limited M.2 slots
    if (m2Slots === 1) {
      score -= 10
    }

    return score
  }

  private checkM2SlotAvailability(storage: StorageModel, motherboard: MotherboardModel): CompatibilityResult {
    const m2Slots = motherboard.m2Slots || 0

    if (m2Slots === 0) {
      return {
        level: 'critical',
        category: 'storage',
        message: 'マザーボードにM.2スロットがありません',
        details: 'NVMe SSDは取り付けできません',
        solutions: [
          'SATA SSDを選択する',
          'M.2スロット付きマザーボードに変更',
          'PCIe M.2拡張カードを使用'
        ],
        score: 0
      }
    }

    if (m2Slots === 1) {
      return {
        level: 'warning',
        category: 'storage',
        message: 'M.2スロットが1つのみです',
        details: '将来の拡張性が制限されます',
        solutions: [
          '複数M.2スロット対応マザーボードを検討',
          'SATA SSDとの組み合わせを計画'
        ],
        score: this.getScore(storage, motherboard)
      }
    }

    return {
      level: 'info',
      category: 'storage',
      message: `M.2スロットが${m2Slots}個利用可能`,
      details: '十分な拡張性があります',
      score: this.getScore(storage, motherboard)
    }
  }

  private checkPCIeLaneCompatibility(storage: StorageModel, motherboard: MotherboardModel): CompatibilityResult | null {
    const storageInterface = storage.interface
    const supportsPCIe4 = this.supportsPCIe4(motherboard)

    if (storageInterface?.includes('PCIe 4.0') && !supportsPCIe4) {
      return {
        level: 'warning',
        category: 'storage',
        message: 'PCIe 4.0 SSDですがPCIe 3.0速度で動作します',
        details: 'マザーボードがPCIe 3.0のため性能が制限されます',
        solutions: [
          'PCIe 4.0対応マザーボードで最大性能を発揮',
          '現在の構成でも十分高速です'
        ]
      }
    }

    if (storageInterface?.includes('PCIe 3.0') && supportsPCIe4) {
      return {
        level: 'info',
        category: 'storage',
        message: 'より高速なPCIe 4.0 SSDでさらなる性能向上が可能',
        details: 'マザーボードはPCIe 4.0に対応しています'
      }
    }

    return null
  }

  private checkFormFactorCompatibility(storage: StorageModel, _motherboard: MotherboardModel): CompatibilityResult | null {
    const formFactor = storage.formFactor

    if (formFactor === 'M.2 2280') {
      return {
        level: 'info',
        category: 'storage',
        message: '標準的なM.2 2280サイズです',
        details: 'ほぼ全てのマザーボードで対応'
      }
    }

    if (formFactor === 'M.2 2242') {
      return {
        level: 'info',
        category: 'storage',
        message: `コンパクトなM.2 ${formFactor}サイズです`,
        details: '小型ケースに適していますが容量が制限される場合があります'
      }
    }

    return null
  }

  private checkBootSupport(_storage: StorageModel, _motherboard: MotherboardModel): CompatibilityResult | null {
    // Most modern motherboards support NVMe boot
    return {
      level: 'info',
      category: 'storage',
      message: 'NVMe SSDからのブートに対応',
      details: '高速な起動が可能です'
    }
  }

  private supportsPCIe4(motherboard: MotherboardModel): boolean {
    const chipset = motherboard.chipset.toLowerCase()

    // Intel PCIe 4.0 support
    if (chipset.includes('z690') || chipset.includes('z790') ||
        chipset.includes('b660') || chipset.includes('b760') ||
        chipset.includes('h670') || chipset.includes('h770')) {
      return true
    }

    // AMD PCIe 4.0 support
    if (chipset.includes('x570') || chipset.includes('b550') ||
        chipset.includes('x670') || chipset.includes('b650')) {
      return true
    }

    return false
  }
}

/**
 * SATA Storage Compatibility Checker
 */
export class SATAStorageCompatibilityChecker implements CompatibilityChecker<StorageModel, MotherboardModel> {
  check(storage: StorageModel, motherboard: MotherboardModel, _context?: CompatibilityContext): CompatibilityResult[] {
    const results: CompatibilityResult[] = []

    if (storage.category === 'NVMe SSD') {
      return results // Only check SATA drives
    }

    // SATA port availability
    const portResult = this.checkSATAPortAvailability(storage, motherboard)
    results.push(portResult)

    // Performance optimization
    const perfResult = this.analyzePerformanceOptimization(storage, motherboard)
    if (perfResult) results.push(perfResult)

    return results
  }

  getScore(storage: StorageModel, motherboard: MotherboardModel): number {
    if (storage.category === 'NVMe SSD') return 100

    const sataPorts = motherboard.sataPorts ?? 4
    const supportsSATA3 = this.supportsSATA3(motherboard)

    let score = 100

    if (sataPorts < 2) {
      score -= 30 // Very limited expansion
    } else if (sataPorts < 4) {
      score -= 10 // Limited expansion
    }

    if (!supportsSATA3) {
      score -= 20 // Older SATA standard
    }

    return score
  }

  private checkSATAPortAvailability(storage: StorageModel, motherboard: MotherboardModel): CompatibilityResult {
    const sataPorts = motherboard.sataPorts ?? 4

    if (sataPorts === 0) {
      return {
        level: 'critical',
        category: 'storage',
        message: 'SATAポートがありません',
        details: 'SATA SSD/HDDは接続できません',
        solutions: [
          'M.2 NVMe SSDを選択',
          'SATAポート付きマザーボードに変更'
        ],
        score: 0
      }
    }

    if (sataPorts <= 2) {
      return {
        level: 'warning',
        category: 'storage',
        message: `SATAポートが${sataPorts}個と少ないです`,
        details: '複数のストレージ接続が制限されます',
        solutions: [
          'M.2 SSDとの組み合わせを検討',
          'より多くのSATAポートを持つマザーボードを検討'
        ],
        score: this.getScore(storage, motherboard)
      }
    }

    return {
      level: 'info',
      category: 'storage',
      message: `SATAポートが${sataPorts}個利用可能`,
      details: '十分な拡張性があります',
      score: this.getScore(storage, motherboard)
    }
  }

  private analyzePerformanceOptimization(storage: StorageModel, motherboard: MotherboardModel): CompatibilityResult | null {
    const supportsSATA3 = this.supportsSATA3(motherboard)

    if (!supportsSATA3) {
      return {
        level: 'warning',
        category: 'storage',
        message: 'SATA 3.0に対応していません',
        details: 'ストレージ性能が制限されます',
        solutions: [
          'SATA 3.0対応マザーボードで最大性能を発揮',
          'NVMe SSDへのアップグレードを検討'
        ]
      }
    }

    if (storage.category === 'HDD') {
      return {
        level: 'info',
        category: 'storage',
        message: '大容量ストレージとして適しています',
        details: 'システムドライブにはSSDを推奨',
        solutions: [
          'OS用にSSDを追加することで大幅な性能向上',
          'データ保存用として最適'
        ]
      }
    }

    return null
  }

  private supportsSATA3(_motherboard: MotherboardModel): boolean {
    // Modern motherboards generally support SATA 3.0
    // This is a simplified check - in reality would need to check chipset specs
    return true
  }
}

/**
 * Storage Performance Optimizer
 */
export class StoragePerformanceOptimizer {
  analyzeStorageConfiguration(storageDevices: StorageModel[], motherboard: MotherboardModel, _cpu?: CPUModel): {
    score: number
    optimizations: string[]
    warnings: string[]
    bootDriveRecommendation?: string
  } {
    const optimizations: string[] = []
    const warnings: string[] = []
    let score = 100

    // Check for optimal boot drive configuration
    const nvmeCount = storageDevices.filter(s => s.category === 'NVMe SSD').length
    const sataCount = storageDevices.filter(s => s.category === 'SATA SSD' || s.category === 'HDD').length

    if (nvmeCount === 0 && sataCount > 0) {
      score -= 20
      optimizations.push('NVMe SSDをシステムドライブとして追加すると大幅な性能向上が期待できます')
    }

    // Check for storage diversity
    const hasHDD = storageDevices.some(s => s.category === 'HDD')
    const hasSSD = storageDevices.some(s => s.category.includes('SSD'))

    if (hasHDD && !hasSSD) {
      score -= 30
      warnings.push('システムドライブにSSDを使用することを強く推奨します')
    }

    // Check M.2 slot utilization
    const m2Slots = motherboard.m2Slots || 0
    if (nvmeCount > 0 && nvmeCount < m2Slots) {
      optimizations.push(`M.2スロットをさらに活用できます (${nvmeCount}/${m2Slots}使用中)`)
    }

    // Boot drive recommendation
    let bootDriveRecommendation: string | undefined
    if (nvmeCount > 0) {
      const fastestNVMe = storageDevices
        .filter(s => s.category === 'NVMe SSD')
        .sort((a, b) => (b.sequentialRead || 0) - (a.sequentialRead || 0))[0]
      bootDriveRecommendation = `${fastestNVMe.manufacturer} ${fastestNVMe.model} をシステムドライブに推奨`
    } else if (sataCount > 0) {
      const fastestSATA = storageDevices
        .filter(s => s.category === 'SATA SSD')
        .sort((a, b) => (b.sequentialRead || 0) - (a.sequentialRead || 0))[0]
      if (fastestSATA) {
        bootDriveRecommendation = `${fastestSATA.manufacturer} ${fastestSATA.model} をシステムドライブに推奨`
      }
    }

    return {
      score,
      optimizations,
      warnings,
      ...(bootDriveRecommendation != null ? { bootDriveRecommendation } : {})
    }
  }
}

/**
 * Storage Compatibility Engine
 * Coordinates all storage compatibility checks
 */
export class StorageCompatibilityEngine {
  private m2Checker = new M2StorageCompatibilityChecker()
  private sataChecker = new SATAStorageCompatibilityChecker()
  private performanceOptimizer = new StoragePerformanceOptimizer()

  checkStorageCompatibility(storage: StorageModel, motherboard: MotherboardModel, context?: CompatibilityContext): CompatibilityResult[] {
    const results: CompatibilityResult[] = []

    if (storage.category === 'NVMe SSD') {
      results.push(...this.m2Checker.check(storage, motherboard, context))
    } else {
      results.push(...this.sataChecker.check(storage, motherboard, context))
    }

    return results
  }

  getStorageCompatibilityScore(storage: StorageModel, motherboard: MotherboardModel): number {
    if (storage.category === 'NVMe SSD') {
      return this.m2Checker.getScore(storage, motherboard)
    } else {
      return this.sataChecker.getScore(storage, motherboard)
    }
  }

  analyzeStorageConfiguration(storageDevices: StorageModel[], motherboard: MotherboardModel, cpu?: CPUModel) {
    return this.performanceOptimizer.analyzeStorageConfiguration(storageDevices, motherboard, cpu)
  }

  generateStorageReport(storageDevices: StorageModel[], motherboard: MotherboardModel, cpu?: CPUModel): {
    compatible: boolean
    overallScore: number
    configurationAnalysis: ReturnType<StoragePerformanceOptimizer['analyzeStorageConfiguration']>
    individualResults: Array<{
      storage: StorageModel
      score: number
      results: CompatibilityResult[]
    }>
    recommendations: string[]
  } {
    const individualResults = storageDevices.map(storage => ({
      storage,
      score: this.getStorageCompatibilityScore(storage, motherboard),
      results: this.checkStorageCompatibility(storage, motherboard)
    }))

    const configurationAnalysis = this.analyzeStorageConfiguration(storageDevices, motherboard, cpu)

    const overallScore = individualResults.length > 0
      ? (individualResults.reduce((sum, result) => sum + result.score, 0) / individualResults.length + configurationAnalysis.score) / 2
      : 100

    const recommendations: string[] = [
      ...configurationAnalysis.optimizations,
      ...configurationAnalysis.warnings
    ]

    // Add individual storage recommendations
    individualResults.forEach(result => {
      result.results.forEach(r => {
        if (r.solutions) {
          recommendations.push(...r.solutions)
        }
      })
    })

    return {
      compatible: overallScore > 60,
      overallScore,
      configurationAnalysis,
      individualResults,
      recommendations: [...new Set(recommendations)]
    }
  }
}